import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../data";
import { ASSETS, X_URL } from "../config";
import { XIcon } from "./SocialIcons";
import { StatusDot } from "./primitives";

const linkCls = (isActive) =>
  `font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 ${
    isActive ? "text-pulse-soft" : "text-fog-400 hover:text-fog-100"
  }`;

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "border-b border-white/[0.08] bg-ink-900/85 backdrop-blur-md" : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-5 md:px-8">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="SYNAPTIX" className="h-8 w-8 object-contain" />
            <span className="font-display text-sm font-bold tracking-[0.3em] text-fog-100">SYNAPTIX</span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`} className={({ isActive }) => linkCls(isActive)}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-4 lg:flex">
            <a href={X_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-social-x"
              className="text-fog-600 transition-colors hover:text-fog-100">
              <XIcon className="h-3.5 w-3.5" />
            </a>
            <button
              onClick={() => navigate("/terminal")}
              data-testid="nav-launch-app"
              className="group flex items-center gap-2 border border-pulse/30 bg-pulse/10 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-pulse-soft transition-all duration-200 hover:bg-pulse/20 hover:shadow-[0_0_24px_rgba(0,200,5,0.15)]"
            >
              Launch App
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>

          <button
            className="flex h-10 w-10 items-center justify-center border border-white/10 text-fog-100 lg:hidden"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 flex flex-col bg-ink-900/97 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-1 flex-col justify-center gap-2 px-8 pt-16">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block border-b border-white/[0.06] py-4 font-display text-3xl font-bold tracking-tight ${
                        isActive ? "text-pulse-soft" : "text-fog-100"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-8 flex items-center gap-6">
                <a href={X_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-social-x" className="text-fog-400"><XIcon className="h-5 w-5" /></a>
              </div>
            </div>
            <div className="flex items-center gap-2 border-t border-white/[0.08] px-8 py-5">
              <StatusDot />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-fog-600">Synaptix Core — System Online</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
